import { Alert, Chip, Divider, Stack, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { SLA_ACTIVITIES_TEXT_KEYS } from "model/slaActivitiesModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import type { Task } from "model/activitiesModel";
import React from "react";
import { useSlaActivityForTask } from "./slaActivitiesContext";
import SlaInstanceSummaryLine from "./SlaInstanceSummaryLine";

/**
 * **Dettaglio SLA dentro il dialog attivita' esistente** (sez. 5 F3c: "dettaglio
 * con originale/vigente e override auditato"). Montato da
 * `TaskDetailDialog` — il dettaglio che la pagina attivita' ha gia' e che la
 * rotta di atterraggio `myactivities/task/:taskId` apre (decisione 2): nessun
 * dialog nuovo.
 *
 * Cosa rende:
 * - **originale e vigente entrambe visibili**, ciascuna con la propria chiave e
 *   nella timezone dichiarata dalla riga: le revisioni non cancellano la promessa
 *   originale (contratto C1);
 * - la vigente e' **etichettata come modificata** (`overridden`) quando il
 *   payload lo dichiara, con l'etichetta riusata dal Monitor
 *   (`sla.monitor.overridden`): nessuna label duplicata;
 * - il **motivo** dell'override non e' nel payload istanza
 *   (`SLA_ACTIVITY_OVERRIDE_REASON_SOURCE`): la sezione lo **dichiara non
 *   disponibile** invece di mostrare un testo inventato. E' il residuo dichiarato
 *   di F3c, non una simulazione;
 * - la **revisione** dell'istanza e il **livello di escalation** raggiunto
 *   ("n/m", sez. 14.1) come informazione aggiuntiva: il breach resta un breach.
 */
interface SlaTaskSlaDetailProps {
  task?: Task | null;
}

const PREFIX = "sla-activities-detail";

const SlaTaskSlaDetail: React.FC<SlaTaskSlaDetailProps> = ({ task }) => {
  const translations = useTranslations();
  const view = useSlaActivityForTask(task ?? null);

  if (!view) return null;

  const { instance, detail, countdown } = view;

  return (
    <Stack
      spacing={0.5}
      data-testid={PREFIX}
      data-instance-id={instance.id}
      data-health={detail.health === null ? "" : detail.health}
      sx={{ mb: 2 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.detailTitle)}
        </Typography>
        {detail.healthKey && (
          <Chip
            size="small"
            label={getTranslation(translations, detail.healthKey)}
            data-testid={`${PREFIX}-health`}
            data-health-icon={detail.health === null ? "" : detail.health}
          />
        )}
        {detail.overridden && (
          <Chip
            size="small"
            variant="outlined"
            label={getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}
            data-testid={`${PREFIX}-overridden`}
          />
        )}
        <Typography variant="caption" data-testid={`${PREFIX}-escalation`}>
          {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.escalationLabel)}
          {": "}
          {detail.escalationValue ||
            getTranslation(translations, SLA_MONITOR_TEXT_KEYS.escalationNone)}
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 2 }}>
        {/* Promessa originale: le revisioni non la riscrivono. */}
        <Typography
          variant="caption"
          data-testid={`${PREFIX}-original`}
          data-deadline={detail.originalDeadlineAt ?? ""}
          data-timezone={detail.timezoneId}
        >
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.detailOriginal)}
          {": "}
          {detail.originalLabel}
        </Typography>
        {/* Scadenza vigente: quella che il runtime applica. */}
        <Typography
          variant="caption"
          data-testid={`${PREFIX}-effective`}
          data-deadline={detail.effectiveDeadlineAt ?? ""}
          data-timezone={detail.timezoneId}
        >
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.detailEffective)}
          {": "}
          {detail.effectiveLabel}
        </Typography>
        <Typography variant="caption" data-testid={`${PREFIX}-revision`}>
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.detailRevision)}
          {": "}
          {detail.revision}
        </Typography>
      </Stack>

      {/* Countdown lavorativo: numeri del payload (nessun giorno solare). */}
      <Typography
        variant="caption"
        data-testid={`${PREFIX}-countdown`}
        data-working-seconds={countdown.remainingSeconds ?? ""}
        data-overdue-seconds={countdown.overdueSeconds ?? ""}
        data-clock-running={String(countdown.running)}
        aria-live="off"
      >
        {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownLabel)}
        {": "}
        {countdown.remainingSeconds ?? ""}
      </Typography>

      {/* SLA del processo: resta visibile accanto a quello di attivita'. */}
      <SlaInstanceSummaryLine
        view={view.process}
        target="process"
        testId={`${PREFIX}-process`}
      />

      {detail.overrideReasonUnavailable && (
        <Alert severity="info" data-testid={`${PREFIX}-override-reason`}>
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.detailOverrideReason)}
          {": "}
          {getTranslation(
            translations,
            SLA_ACTIVITIES_TEXT_KEYS.detailOverrideReasonUnavailable,
          )}
        </Alert>
      )}

      <Divider />
    </Stack>
  );
};

export default SlaTaskSlaDetail;
